import { cloneDeep, has, isPlainObject, omit } from 'lodash-es';

import { type PartialPropsOf } from '@blaze-runtime/engine-domain';
import { type StyleSheet } from '@blaze-shared/theming-domain';
import { getValueByPath, setValueByPath } from '@blaze-shared/workflow-domain';

import {
  type CanvasElementDefinition,
  type CanvasElementDefinitionInternal,
} from '../../canvas-elements/element-definition';

const isUnder = (path: string, roots: string[]): boolean =>
  roots.some((root) => path === root || path.startsWith(`${root}.`));

const leadsTo = (path: string, roots: string[]): boolean =>
  roots.some((root) => root.startsWith(`${path}.`));

const collectInitialStyleLeafPaths = (
  value: unknown,
  path: string,
  includePaths: string[],
  excludePaths: string[],
  leafPaths: string[],
): void => {
  if (path && isUnder(path, excludePaths)) {
    return;
  }

  if (isPlainObject(value)) {
    const keys = Object.keys(value as Record<string, unknown>);

    for (const key of keys) {
      const childPath = path ? `${path}.${key}` : key;
      if (isUnder(childPath, includePaths) || leadsTo(childPath, includePaths)) {
        collectInitialStyleLeafPaths(
          (value as Record<string, unknown>)[key],
          childPath,
          includePaths,
          excludePaths,
          leafPaths,
        );
      }
    }
    return;
  }

  if (value !== undefined && path && isUnder(path, includePaths)) {
    leafPaths.push(path);
  }
};

/**
 * Applies all initial props that live under the definition's style paths.
 * Used when style props are cleaned without a style sheet taking over (e.g. style reset),
 * so the element gets back its creation-time style defaults.
 * @param props
 * @param definition
 */
export function applyInitialStyleProps<T>(
  props: T,
  definition: CanvasElementDefinition | CanvasElementDefinitionInternal,
): T {
  const { initial, styleIncludePaths = [], styleExcludePaths = [] } = definition;

  if (!initial || !styleIncludePaths.length) {
    return props;
  }

  const leafPaths: string[] = [];
  collectInitialStyleLeafPaths(initial, '', styleIncludePaths, styleExcludePaths, leafPaths);
  if (!leafPaths.length) {
    return props;
  }

  const updatedProps = cloneDeep(props ?? {}) as T;

  for (const path of leafPaths) {
    setValueByPath(updatedProps, path, cloneDeep(getValueByPath(initial, path)));
  }

  return updatedProps;
}

/**
 * Applies missing initial props to the element.
 * Styles should carry initial props with them, but if they don't for some reason,
 * this function will find them and apply them to the element.
 * @param props
 * @param definition
 * @param styleSheet
 */
export function applyMissingInitialProps<T extends PartialPropsOf = PartialPropsOf>(
  props: T,
  definition: CanvasElementDefinition | CanvasElementDefinitionInternal,
  styleSheet: StyleSheet,
): T {
  const { initial, styleIncludePaths = [], styleExcludePaths = [] } = definition;

  if (!initial || !styleIncludePaths.length) {
    return props;
  }

  const leafPaths: string[] = [];
  collectInitialStyleLeafPaths(initial, '', styleIncludePaths, styleExcludePaths, leafPaths);
  if (!leafPaths.length) {
    return props;
  }

  const sheetBaseProps = styleSheet.data?.[0] ?? {};
  let updatedProps = cloneDeep(props ?? {}) as T;

  for (const path of leafPaths) {
    if (has(sheetBaseProps, path)) {
      updatedProps = omit(updatedProps, path) as T;
    } else {
      const data = getValueByPath(initial, path);
      const cloned = cloneDeep(data);
      setValueByPath(updatedProps, path, cloned);
    }
  }

  return updatedProps;
}
